// Demands — PI Platform outline v2 §1.7.
//
// Sits under Demand Content on the Documents tab, because that is the
// order the work happens in: compose the chronology, generate the
// letter, send it, then live with the clock.
//
// The card exists to make three facts impossible to lose:
//
//   1. Whether delivery can actually be PROVEN. Carriers dispute
//      receipt, and without independent proof the deadline argument
//      rests on our own say-so.
//   2. Where the clock is. A demand deadline that passes unnoticed is
//      a bad-faith posture nobody claimed.
//   3. Whether the limits were verified when the demand went out. A
//      policy-limits demand built on a phone call can ask for less than
//      the policy.
//
// The warnings from `demand_posture` are shown prominently rather than
// tucked away, because they are the point — being told, while it can
// still be fixed, that this demand will be contested.

import React, { useCallback, useEffect, useState } from "react";
import { C } from "../constants.js";
import { apiFetch } from "../api.js";
import { Spinner } from "../components/Spinner.jsx";

const DEMAND_TYPE_LABELS = {
  policy_limits: "Policy limits", time_limited: "Time-limited",
  standard: "Standard", supplemental: "Supplemental",
};

const STATUS_LABELS = {
  draft: "Draft", sent: "Sent", delivered: "Delivered",
  responded: "Responded", expired: "Expired", withdrawn: "Withdrawn",
  accepted: "Accepted",
};

// Ordered strongest proof first, so the habit the list encourages is
// the one that survives a dispute.
const DELIVERY_METHODS = [
  ["certified_mail", "Certified mail"],
  ["fax", "Fax"],
  ["courier", "Courier"],
  ["email", "Email (no independent proof)"],
  ["hand_delivery", "Hand delivery (no independent proof)"],
  ["portal", "Carrier portal (no independent proof)"],
];

const RESPONSE_TYPES = [
  ["accepted", "Accepted"], ["counter_offer", "Counter-offer"],
  ["rejected", "Rejected"], ["partial", "Partial"],
];

const EVENT_LABELS = {
  drafted: "Drafted", sent: "Sent", delivery_confirmed: "Delivery confirmed",
  reminder_sent: "Reminder sent", response_received: "Response received",
  deadline_expired: "Deadline expired", withdrawn: "Withdrawn", note: "Note",
};

const money = (v) =>
  v === null || v === undefined ? "—" : `$${Number(v).toLocaleString(undefined, {
    minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const dateOnly = (v) => (v ? String(v).slice(0, 10) : null);

const EMPTY = { demand_type: "policy_limits", amount_demanded: "", recipient_name: "" };

export function DemandsCard({ matterId, showNotification }) {
  const [demands, setDemands] = useState(null);
  const [posture, setPosture] = useState(null);
  const [draft, setDraft] = useState(EMPTY);
  const [adding, setAdding] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const [detail, setDetail] = useState({});

  const load = useCallback(async () => {
    try {
      const d = await apiFetch(`/api/matters/${matterId}/demands`);
      setDemands(d.data || []);
      setPosture(d.posture || null);
    } catch (e) {
      showNotification?.(`Demands failed to load: ${e.message}`, "error");
      setDemands([]);
    }
  }, [matterId, showNotification]);

  useEffect(() => { load(); }, [load]);

  const openTrail = async (id) => {
    if (expanded === id) { setExpanded(null); return; }
    setExpanded(id);
    if (!detail[id]) {
      try {
        // Await first: the state updater is a plain function, so an
        // `await` inside it is a syntax error, not a race.
        const full = await apiFetch(`/api/demands/${id}`);
        setDetail(prev => ({ ...prev, [id]: full }));
      } catch (e) { showNotification?.(`Couldn't load it: ${e.message}`, "error"); }
    }
  };

  const refresh = async (id) => {
    setDetail(prev => { const n = { ...prev }; delete n[id]; return n; });
    await load();
    if (expanded === id) await openTrail(id);
  };

  const addDemand = async () => {
    if (!draft.amount_demanded) {
      showNotification?.("How much is being demanded?", "warning");
      return;
    }
    try {
      await apiFetch(`/api/matters/${matterId}/demands`, {
        method: "POST",
        body: JSON.stringify({
          demand_type: draft.demand_type,
          amount_demanded: Number(draft.amount_demanded),
          recipient_name: draft.recipient_name.trim() || null,
        }),
      });
      setDraft(EMPTY); setAdding(false);
      await load();
    } catch (e) { showNotification?.(`Couldn't draft it: ${e.message}`, "error"); }
  };

  const send = async (d) => {
    const idx = window.prompt(
      "How is it going out?\n\n" +
      DELIVERY_METHODS.map(([, l], i) => `${i + 1}. ${l}`).join("\n") +
      "\n\nEnter 1-6:");
    if (!idx) return;
    const picked = DELIVERY_METHODS[Number(idx) - 1];
    if (!picked) { showNotification?.("Pick 1-6.", "warning"); return; }
    const ref = window.prompt(
      "Tracking / confirmation number (this is what proves delivery — " +
      "leave blank if there isn't one):");
    const days = window.prompt("Days to respond (blank for no deadline):", "30");
    try {
      const r = await apiFetch(`/api/demands/${d.id}/send`, {
        method: "POST",
        body: JSON.stringify({
          delivery_method: picked[0],
          delivery_reference: ref || null,
          response_days: days ? Number(days) : null,
        }),
      });
      showNotification?.(
        r.response_deadline
          ? `Sent. Response due ${dateOnly(r.response_deadline)}.`
          : "Sent.", "success");
      await refresh(d.id);
    } catch (e) {
      // The two deliberate refusals — over-coverage and re-sending —
      // arrive here and are worth reading in full.
      showNotification?.(e.message, "error");
    }
  };

  const confirmDelivery = async (d) => {
    const ref = window.prompt(
      "Tracking / confirmation number, if you have one now:",
      d.delivery_reference || "");
    try {
      await apiFetch(`/api/demands/${d.id}/delivery`, {
        method: "POST", body: JSON.stringify({ delivery_reference: ref || null }),
      });
      await refresh(d.id);
    } catch (e) { showNotification?.(`Failed: ${e.message}`, "error"); }
  };

  const recordResponse = async (d) => {
    const idx = window.prompt(
      "What came back?\n\n" +
      RESPONSE_TYPES.map(([, l], i) => `${i + 1}. ${l}`).join("\n") + "\n\nEnter 1-4:");
    if (!idx) return;
    const picked = RESPONSE_TYPES[Number(idx) - 1];
    if (!picked) { showNotification?.("Pick 1-4.", "warning"); return; }
    const amount = window.prompt("Amount, if they put a number on it:");
    try {
      const r = await apiFetch(`/api/demands/${d.id}/response`, {
        method: "POST",
        body: JSON.stringify({
          response_type: picked[0],
          amount: amount ? Number(amount) : null,
        }),
      });
      if (r.responded_after_deadline) {
        showNotification?.("Recorded — and it came in after the deadline.", "warning");
      }
      await refresh(d.id);
    } catch (e) { showNotification?.(`Failed: ${e.message}`, "error"); }
  };

  if (demands === null) {
    return React.createElement("div", { style: cardStyle },
      React.createElement("div", { style: { display: "flex", alignItems: "center", gap: "8px" } },
        React.createElement(Spinner, { size: 16 }),
        React.createElement("span", { style: { fontSize: "13px", color: C.textMuted } },
          "Loading demands…")));
  }

  const inp = { padding: "7px 10px", borderRadius: "6px", border: `1px solid ${C.warmGray}`,
                fontSize: "13px", background: "#fff" };

  return React.createElement("div", { style: cardStyle },
    React.createElement("div", {
      style: { display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: "12px" },
    },
      React.createElement("div", null,
        React.createElement("h2", { style: h2Style }, "Demands"),
        React.createElement("div", { style: { fontSize: "13px", color: C.textMuted, marginTop: "4px" } },
          demands.length === 0
            ? "Nothing sent yet."
            : `${demands.length} demand${demands.length === 1 ? "" : "s"}`
              + (posture?.open_count ? ` · ${posture.open_count} awaiting a response` : "")
              + (posture?.expired_count ? ` · ${posture.expired_count} expired` : "")),
      ),
      React.createElement("button", {
        onClick: () => setAdding(a => !a),
        style: { padding: "7px 14px", borderRadius: "8px", border: `1px solid ${C.navy}`,
                 background: "transparent", color: C.navy, fontSize: "12px",
                 fontWeight: 600, cursor: "pointer", flexShrink: 0 },
      }, adding ? "Cancel" : "Draft demand"),
    ),

    // The posture — what this record would look like under scrutiny.
    posture && posture.unanswered_with_proof.length > 0 && React.createElement("div", {
      style: { marginTop: "12px", padding: "12px 14px", background: `${C.gold}12`,
               border: `1px solid ${C.gold}40`, borderRadius: "8px" },
    },
      React.createElement("div", { style: { fontSize: "12px", fontWeight: 700, color: C.navy } },
        `${posture.unanswered_with_proof.length} demand(s) provably delivered, expired, and unanswered`),
      React.createElement("div", { style: { fontSize: "12px", color: C.textMuted, marginTop: "3px" } },
        "This is the record a bad-faith argument rests on — delivery proven, "
        + "deadline passed, nothing received."),
      ...posture.unanswered_with_proof.map(u => React.createElement("div", {
        key: u.id, style: { fontSize: "12px", color: C.textDark, marginTop: "5px" },
      }, `${u.recipient_name || "Carrier"} · ${money(u.amount_demanded)} · deadline `
         + `${dateOnly(u.response_deadline)}`)),
    ),

    adding && React.createElement("div", {
      style: { display: "flex", gap: "8px", flexWrap: "wrap", alignItems: "center",
               padding: "12px 14px", background: C.warmWhite, borderRadius: "8px", marginTop: "12px" },
    },
      React.createElement("select", {
        value: draft.demand_type, style: { ...inp, minWidth: "150px" },
        onChange: e => setDraft({ ...draft, demand_type: e.target.value }),
      }, ...Object.entries(DEMAND_TYPE_LABELS).map(([v, l]) =>
        React.createElement("option", { key: v, value: v }, l))),
      React.createElement("input", {
        type: "text", value: draft.recipient_name, placeholder: "To (carrier / adjuster)",
        onChange: e => setDraft({ ...draft, recipient_name: e.target.value }),
        style: { ...inp, flex: "1 1 190px" },
      }),
      React.createElement("input", {
        type: "number", value: draft.amount_demanded, placeholder: "Amount demanded",
        onChange: e => setDraft({ ...draft, amount_demanded: e.target.value }),
        style: { ...inp, width: "160px" },
      }),
      React.createElement("button", {
        onClick: addDemand,
        style: { padding: "8px 16px", borderRadius: "8px", border: "none", background: C.gold,
                 color: "#fff", fontSize: "12px", fontWeight: 700, cursor: "pointer" },
      }, "Draft"),
    ),

    demands.length > 0 && React.createElement("div", {
      style: { border: `1px solid ${C.warmGray}60`, borderRadius: "8px",
               overflow: "hidden", marginTop: "14px" },
    },
      ...demands.map((d, i) => React.createElement("div", {
        key: d.id,
        style: { padding: "11px 13px", borderTop: i === 0 ? "none" : `1px solid ${C.warmGray}40` },
      },
        React.createElement("div", {
          style: { display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap" },
        },
          React.createElement("div", { style: { flex: "1 1 190px", minWidth: 0 } },
            React.createElement("div", { style: { fontSize: "13px", fontWeight: 600, color: C.navy } },
              d.recipient_name || "Carrier"),
            React.createElement("div", { style: { fontSize: "11px", color: C.textMuted, marginTop: "2px" } },
              DEMAND_TYPE_LABELS[d.demand_type] || d.demand_type,
              " · ", STATUS_LABELS[d.status] || d.status,
              d.sent_date ? ` · sent ${dateOnly(d.sent_date)}` : "")),

          React.createElement("div", { style: { fontSize: "13px", fontWeight: 600, color: C.navy, whiteSpace: "nowrap" } },
            money(d.amount_demanded)),

          // The clock, in words rather than a colour.
          d.response_deadline && React.createElement("span", {
            style: { fontSize: "11px", fontWeight: 600, whiteSpace: "nowrap",
                     color: d.is_expired ? C.navy : C.textMuted },
          }, d.is_expired
              ? `expired ${dateOnly(d.response_deadline)}`
              : `${d.days_remaining} day${d.days_remaining === 1 ? "" : "s"} left`),

          // Whether it can be proven. The single most useful thing on
          // the row.
          (d.status !== "draft") && React.createElement("span", {
            title: d.has_independent_proof
              ? "Independent proof of delivery on file"
              : "No independent proof — the carrier can dispute receipt",
            style: {
              fontSize: "10px", fontWeight: 700, padding: "3px 9px", borderRadius: "9999px",
              textTransform: "uppercase", letterSpacing: "0.03em", whiteSpace: "nowrap",
              background: d.has_independent_proof ? `${C.gold}20` : `${C.warmGray}60`,
              color: d.has_independent_proof ? C.navy : C.textMuted,
            },
          }, d.has_independent_proof ? "Proof on file" : "Unproven"),

          d.status === "draft" && _quiet("Send", () => send(d)),
          (d.status === "sent") && _quiet("Confirm delivery", () => confirmDelivery(d)),
          ["sent", "delivered", "expired"].includes(d.status)
            && _quiet("Record response", () => recordResponse(d)),
          _quiet(expanded === d.id ? "Hide" : "History", () => openTrail(d.id)),
        ),

        expanded === d.id && React.createElement("div", {
          style: { marginTop: "9px", paddingTop: "9px", borderTop: `1px solid ${C.warmGray}40` },
        },
          !detail[d.id]
            ? React.createElement("div", { style: { fontSize: "12px", color: C.textMuted } }, "Loading…")
            : React.createElement("div", { style: { display: "flex", flexDirection: "column", gap: "4px" } },
                ...(detail[d.id].events || []).map(ev => React.createElement("div", {
                  key: ev.id, style: { display: "flex", gap: "10px", fontSize: "12px", color: C.textDark },
                },
                  React.createElement("span", { style: { color: C.textLight, minWidth: "88px" } },
                    dateOnly(ev.occurred_at) || ""),
                  React.createElement("span", { style: { fontWeight: 600, minWidth: "150px" } },
                    EVENT_LABELS[ev.event_type] || ev.event_type),
                  ev.amount !== null && React.createElement("span", { style: { minWidth: "90px" } },
                    money(ev.amount)),
                  ev.notes && React.createElement("span", { style: { color: C.textMuted } }, ev.notes),
                )),
                detail[d.id].limits_were_verified === false && React.createElement("div", {
                  style: { fontSize: "12px", color: C.navy, marginTop: "6px" },
                }, `Sent against unverified limits of ${money(detail[d.id].policy_limits_snapshot)}.`),
              ),
        ),
      )),
    ),

    posture && (posture.warnings || []).length > 0 && React.createElement("ul", {
      style: { margin: "12px 0 0", paddingLeft: "18px", fontSize: "12px",
               color: C.textMuted, lineHeight: 1.6 },
    }, ...posture.warnings.map((w, i) => React.createElement("li", { key: i }, w))),
  );
}

const cardStyle = {
  background: "#fff", borderRadius: "12px", padding: "20px 24px",
  border: "1px solid #E8E3DC", marginBottom: "20px",
};
const h2Style = {
  fontSize: "13px", color: "#6B6660", textTransform: "uppercase",
  letterSpacing: "0.08em", fontWeight: 600, margin: 0,
};

function _quiet(label, onClick) {
  if (!label) return null;
  return React.createElement("button", {
    key: label, onClick,
    style: {
      border: "1px solid #E8E3DC", background: "#fff", borderRadius: "6px",
      padding: "5px 11px", fontSize: "11px", fontWeight: 600,
      color: "#1B2A4A", cursor: "pointer", flexShrink: 0,
    },
  }, label);
}
